import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../api';

function fmtBytes(bytes) {
  if (!Number.isFinite(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtTime(ts) {
  if (!Number.isFinite(ts)) return '—';
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return '—';
  }
}

export default function GoogleAdsExports() {
  const token = useMemo(() => localStorage.getItem('token'), []);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [files, setFiles] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}ads/exports`, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setFiles(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load exports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async (period) => {
    setGenerating(period);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}ads/exports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ period }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setSuccess(`Generated ${data?.file?.name || 'file'} (${data?.file?.count ?? 0} clients).`);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to generate export');
    } finally {
      setGenerating('');
    }
  };

  const handleDownload = async (name) => {
    setError('');
    try {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API_BASE_URL}ads/exports/download/${encodeURIComponent(name)}`, {
        headers: {
          Accept: 'text/csv',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Download failed');
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1280, mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Google Ads exports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          UTF-8 CSV (no BOM) for Google Ads Customer Match — headers exactly: Email, Phone, First Name,
          Last Name, Country, Zip. Phones in E.164 (+44…), emails lowercase, Country GB, UK postcodes when available.
          From completed jobs only. Google recommends at least 100 rows.
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Button
            variant="contained"
            onClick={() => generate('daily')}
            disabled={!token || !!generating}
          >
            {generating === 'daily' ? 'Generating…' : 'Generate daily'}
          </Button>
          <Button
            variant="contained"
            onClick={() => generate('weekly')}
            disabled={!token || !!generating}
          >
            {generating === 'weekly' ? 'Generating…' : 'Generate weekly'}
          </Button>
          <Button
            variant="contained"
            onClick={() => generate('monthly')}
            disabled={!token || !!generating}
          >
            {generating === 'monthly' ? 'Generating…' : 'Generate monthly'}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
          <Chip size="small" label="CSV UTF-8" />
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.path || f.name}>
                  <TableCell>{f.name}</TableCell>
                  <TableCell>{fmtBytes(f.size)}</TableCell>
                  <TableCell>{fmtTime(f.last_modified)}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(f.name)}
                      disabled={!token}
                    >
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {files.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No exports yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

